import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { config } from '@/app/config';
import { Icon, Logo } from '@/components/ui';
import { AnnouncementBanner } from '@/features/announcements/AnnouncementBanner';
import { cn } from '@/utils/cn';

import { initials } from './format';

/**
 * The signed-in application shell: rail, top bar, page slot.
 *
 * One component for admin, student and company. Before this each layout drew
 * its own chrome, and the two thin ones had drifted into a flat list of
 * unlabelled links -- sixteen of them, in a grid column that scrolled away
 * with the page. What differs between the three roles is *data*: which
 * destinations, which counts, which account block. That is all props now, and
 * the chrome is written once.
 *
 * Everything renders inside `.app-shell`, which re-points the brand ramp (see
 * `index.css`). That is why nothing here sets a purple: `bg-brand-600` is
 * violet in this subtree and stays blue everywhere else.
 */

/**
 * The destination a path belongs to, longest match first.
 *
 * Not `startsWith` in list order: `/admin/billing` is a prefix of
 * `/admin/billing/subscriptions`, so the first match lights the wrong rail
 * entry and names the wrong page in the breadcrumb. The most specific route
 * containing the path is the one the reader is on.
 */
/**
 * Whether a menu entry is the one the reader is on, by address. Two entries
 * may open the same page (Recommendations, and For you under My internships),
 * and then both are lit -- with My internships as For you's parent.
 */
function isCurrent(entry, current) {
  return Boolean(current) && entry.to === current.to;
}

function findCurrent(groups, pathname, search = '') {
  // `also` lists further paths an entry stands for -- the tabs of a page whose
  // tabs have their own URLs. An entry may also carry a query (`?view=calendar`)
  // when it opens one view of a page: it wins over the plain entry only while
  // that query is in the address. Each candidate is ranked by what it matched.
  const current = new URLSearchParams(search);
  return groups
    .flatMap((group) =>
      group.items
        .flatMap((item) => [item, ...(item.children ?? [])])
        // `also`: other paths an entry stays lit on (a section spanning pages).
        .flatMap((item) => [item.to, ...(item.also ?? [])].map((to) => ({ item, to }))),
    )
    .map((candidate) => {
      const [path, query = ''] = candidate.to.split('?');
      const wanted = [...new URLSearchParams(query)];
      return { ...candidate, path, specificity: wanted.length, wanted };
    })
    .filter(({ path }) => pathname === path || pathname.startsWith(`${path}/`))
    .filter(({ wanted }) => wanted.every(([key, value]) => current.get(key) === value))
    .sort((a, b) => b.path.length - a.path.length || b.specificity - a.specificity)[0]?.item;
}

/**
 * Whether the desktop rail is collapsed to icons, remembered per browser.
 *
 * A preference, not state anyone else needs: storage can be missing or throw
 * (private windows, blocked site data), and then the rail simply starts open.
 */
const RAIL_KEY = 'jw.shell.rail-collapsed';

function useCollapsedRail() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(RAIL_KEY) === '1';
    } catch {
      return false;
    }
  });
  const update = (value) => {
    setCollapsed(value);
    try {
      window.localStorage.setItem(RAIL_KEY, value ? '1' : '0');
    } catch {
      /* the preference just will not survive a reload */
    }
  };
  return [collapsed, update];
}

// --------------------------------------------------------------------------- //
// Rail
// --------------------------------------------------------------------------- //

/**
 * Collapsed, each menu group gets its own soft tint, cycling through the
 * theme's four tones -- a column of forty grey squares reads as one block,
 * a few colour families read as sections. Only on `lg`, where collapsing
 * happens; the phone drawer is always the full, labelled list.
 */
const GROUP_TONES = [
  'lg:bg-brand-50 lg:text-brand-600 lg:ring-brand-100',
  'lg:bg-info-50 lg:text-info-600 lg:ring-info-100',
  'lg:bg-success-50 lg:text-success-600 lg:ring-success-100',
  'lg:bg-warning-50 lg:text-warning-600 lg:ring-warning-100',
];

/**
 * A sub-item. In the flyout it carries its own icon on a small tile; in the
 * phone drawer (no hover to open a flyout) it hangs off its parent on a
 * guide line, text only.
 */
function SubNavRow({ item, isActive, inFlyout = false }) {
  return (
    <Link
      to={item.to}
      // Explicit both ways: NavLink compares the path only, so two entries for
      // views of one page would otherwise both claim to be the current page.
      aria-current={isActive ? 'page' : 'false'}
      className={cn(
        'group flex items-center gap-2.5 rounded-md transition',
        inFlyout ? 'px-2 py-1.5 text-[13px]' : 'px-2 py-1 text-[12px]',
        isActive
          ? 'from-brand-600 to-brand-500 shadow-brand-600/20 bg-linear-to-r font-medium text-white shadow-sm'
          : 'text-ink/75 hover:text-ink hover:bg-brand-50',
      )}
    >
      {inFlyout && item.icon && (
        <span
          aria-hidden="true"
          className={cn(
            'grid size-6 shrink-0 place-items-center rounded-md',
            isActive
              ? 'bg-white/20 text-white'
              : 'bg-surface-muted text-ink-muted group-hover:text-brand-700',
          )}
        >
          <Icon name={item.icon} size={13} />
        </span>
      )}
      {/* `title`: a label cut short by the rail's width reads in full on hover. */}
      <span className="flex-1 truncate" title={item.label}>
        {item.label}
      </span>
    </Link>
  );
}

/**
 * A parent's sub-items, as a card beside the rail -- the way the reference
 * opens a menu with children. Fixed-positioned against the row, because the
 * nav scrolls and would clip anything positioned inside it.
 */
function Flyout({ flyout, current, onEnter, onLeave }) {
  const { item } = flyout;
  return (
    <div
      role="menu"
      aria-label={item.label}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      className="border-line bg-surface fixed z-[60] hidden w-56 rounded-lg border p-1.5 shadow-xl lg:block"
      style={{ top: flyout.top, left: flyout.left }}
    >
      {/* A small notch pointing back at the row it belongs to. */}
      <span
        aria-hidden="true"
        className="border-line bg-surface absolute top-4 -left-1.5 size-3 rotate-45 border-b border-l"
      />
      <div className="border-line mb-1 flex items-center gap-2 border-b px-1.5 pt-1 pb-2">
        {item.icon && (
          <span className="from-brand-600 to-brand-500 grid size-6 shrink-0 place-items-center rounded-md bg-linear-to-br text-white">
            <Icon name={item.icon} size={13} />
          </span>
        )}
        <span className="text-ink min-w-0 flex-1 truncate text-[13px] font-semibold">
          {item.label}
        </span>
        <span className="bg-brand-50 text-brand-700 ring-brand-100 rounded-full px-1.5 text-[10px] leading-4 font-semibold tabular-nums ring-1">
          {item.children.length}
        </span>
      </div>
      <div className="space-y-0.5">
        {item.children.map((child) => (
          <SubNavRow key={child.to} item={child} isActive={isCurrent(child, current)} inFlyout />
        ))}
      </div>
    </div>
  );
}

function NavRow({ item, isActive, isAncestor = false, count, collapsed, onTip, onFlyout, tone }) {
  const tip = count > 0 ? `${item.label} · ${count} waiting` : item.label;
  const hasChildren = item.children?.length > 0;
  // A parent opens its flyout on hover or focus (desktop only -- the phone
  // drawer lists sub-items inline). Collapsed, a childless icon shows its
  // label in a floating tip instead.
  const handlers = hasChildren
    ? {
        onMouseEnter: (event) => onFlyout?.(item, event.currentTarget),
        onFocus: (event) => onFlyout?.(item, event.currentTarget),
        onMouseLeave: () => onFlyout?.(null),
      }
    : collapsed
      ? {
          onMouseEnter: (event) => onTip?.(tip, event.currentTarget),
          onFocus: (event) => onTip?.(tip, event.currentTarget),
          onMouseLeave: () => onTip?.(null),
          onBlur: () => onTip?.(null),
        }
      : {};

  return (
    <Link
      to={item.to}
      aria-current={isActive ? 'page' : undefined}
      aria-haspopup={hasChildren ? 'menu' : undefined}
      {...handlers}
      className={cn(
        'group relative flex items-center gap-2.5 rounded-md px-2 py-1 text-[13px] transition',
        // Collapsed, the row is only a hit area; the tile carries every state.
        collapsed &&
          'lg:mx-auto lg:size-10 lg:justify-center lg:bg-none lg:p-0 lg:shadow-none lg:hover:bg-transparent',
        // The active row is a solid gradient fill, as in the reference; a
        // row at rest lifts to white when pointed at, off the tinted rail.
        // A parent whose sub-page is open (My internships on Applied) is
        // filled the same way: it is the menu entry the reader chose.
        isActive || isAncestor
          ? 'from-brand-600 to-brand-500 shadow-brand-600/25 bg-linear-to-r font-medium text-white shadow-md'
          : 'text-ink/80 hover:text-ink hover:bg-surface hover:shadow-xs',
      )}
    >
      {item.icon && (
        // Each destination's glyph sits on its own small white tile: muted at
        // rest, violet on hover, white-on-violet on the page you are on.
        <span
          aria-hidden="true"
          className={cn(
            'grid size-6 shrink-0 place-items-center rounded-md transition duration-200',
            isActive || isAncestor
              ? 'bg-white/20 text-white'
              : 'bg-surface text-ink-muted ring-line group-hover:text-brand-700 ring-1 ring-inset',
            collapsed && [
              'lg:size-9 lg:rounded-lg lg:ring-1 lg:ring-inset',
              isActive || isAncestor
                ? 'lg:from-brand-600 lg:to-brand-500 lg:shadow-brand-600/35 lg:bg-linear-to-br lg:text-white lg:shadow-lg lg:ring-0'
                : [
                    tone,
                    'lg:group-hover:-translate-y-0.5 lg:group-hover:shadow-md lg:group-hover:ring-2',
                  ],
            ],
          )}
        >
          <Icon name={item.icon} size={collapsed ? 18 : 15} />
        </span>
      )}
      {/* `title`: a label cut short by the rail's width reads in full on hover. */}
      <span className={cn('flex-1 truncate', collapsed && 'lg:sr-only')} title={item.label}>
        {item.label}
      </span>
      <span className={cn('contents', collapsed && 'lg:hidden')}>{item.suffix}</span>
      {hasChildren && (
        <Icon
          name="chevron"
          size={12}
          className={cn('hidden shrink-0 opacity-60 lg:block', collapsed && 'lg:hidden')}
        />
      )}
      {count > 0 && (
        <span
          className={cn(
            'rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums',
            isActive || isAncestor ? 'bg-white/25 text-white' : 'bg-warning-100 text-warning-800',
            collapsed && 'lg:hidden',
          )}
        >
          {count}
        </span>
      )}
      {count > 0 && collapsed && (
        <span
          aria-hidden="true"
          className="bg-warning-500 ring-surface absolute top-0.5 right-0.5 hidden size-3 rounded-full ring-2 lg:block"
        />
      )}
    </Link>
  );
}

function NavGroup({ group, current, counts, forceOpen, collapsed, onTip, onFlyout, tone }) {
  const [open, setOpen] = useState(true);
  // While a search is filtering the rail, every surviving group is shown open
  // -- collapsing a group that only exists because it matched would hide the
  // one row the reader was looking for.
  const expanded = forceOpen || open;

  // A group holding the current page never collapses out from under it.
  const holdsCurrent = group.items.some(
    (item) => isCurrent(item, current) || item.children?.some((child) => isCurrent(child, current)),
  );

  return (
    <div>
      {/* Collapsed, a group is a hairline between clusters of icons. */}
      {collapsed && <div className="border-line mx-2 my-3 hidden border-t lg:block" />}
      {/* The reference's section heading: small violet caps, then a hairline
          running to the rail's edge. Still a button -- a group folds away --
          but the chevron only shows when pointed at, so the rest state is
          the quiet rule the reference draws. */}
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={expanded}
        className={cn(
          'group/heading text-brand-700/70 hover:text-brand-700 mt-2 mb-1 flex w-full items-center gap-1.5 rounded-md bg-white/40 px-2 py-1 text-[10px] font-semibold tracking-[0.02em] whitespace-nowrap uppercase transition',
          collapsed && 'lg:hidden',
        )}
      >
        <span className="min-w-0 truncate text-left" title={group.label}>
          {group.label}
        </span>
        <span aria-hidden="true" className="bg-brand-200/60 h-px min-w-2 flex-1" />
        <Icon
          name="chevron"
          size={11}
          className={cn(
            // Out of the layout at rest, so the heading text gets the room;
            // shown on hover or keyboard focus, and always on a folded group.
            'shrink-0 transition',
            expanded
              ? 'hidden rotate-90 group-hover/heading:block group-focus-visible/heading:block'
              : 'block',
          )}
        />
      </button>
      {/* Collapsed, every group shows: there is no heading left to open it by. */}
      {(expanded || holdsCurrent || collapsed) && (
        <div
          className={cn(
            'space-y-0.5',
            collapsed && 'lg:space-y-1',
            !expanded && !holdsCurrent && 'max-lg:hidden',
          )}
        >
          {group.items.map((item) => (
            <div key={item.to}>
              <NavRow
                item={item}
                isActive={isCurrent(item, current)}
                isAncestor={Boolean(item.children?.some((child) => isCurrent(child, current)))}
                count={item.badge ? (counts?.[item.badge] ?? 0) : 0}
                collapsed={collapsed}
                onTip={onTip}
                onFlyout={onFlyout}
                tone={tone}
              />
              {item.children?.length > 0 && (
                // The phone drawer has no hover to open the flyout, so there
                // the sub-items hang off the parent's icon on a guide line.
                <div className="border-line mt-0.5 ml-[19px] space-y-0.5 border-l pl-2 lg:hidden">
                  {item.children.map((child) => (
                    <SubNavRow key={child.to} item={child} isActive={isCurrent(child, current)} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Rail({
  nav,
  current,
  counts,
  sectionLabel,
  user,
  roleLabel,
  profileTo,
  onSignOut,
  collapsed,
}) {
  /** Hidden on a desktop rail that has been collapsed to icons. */
  const full = collapsed ? 'lg:hidden' : '';
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);
  // The collapsed rail's floating label. Fixed-positioned beside the icon,
  // because the nav scrolls and would clip anything positioned inside it.
  const [tip, setTip] = useState(null);

  // The open flyout, if any. Closing waits a moment, so the pointer can cross
  // the gap from the row to the card without the card vanishing under it.
  const [flyout, setFlyout] = useState(null);
  const closeTimer = useRef(null);
  const keepFlyout = () => clearTimeout(closeTimer.current);
  const dropFlyout = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setFlyout(null), 150);
  };
  const showFlyout = (item, element) => {
    if (!item || !element) {
      dropFlyout();
      return;
    }
    keepFlyout();
    const rect = element.getBoundingClientRect();
    setFlyout({ item, top: rect.top - 6, left: rect.right + 10 });
  };
  useEffect(() => () => clearTimeout(closeTimer.current), []);
  // A flyout left open after a click would cover the page it just opened.
  useEffect(() => setFlyout(null), [current?.to]);

  const showTip = (label, element) => {
    if (!label || !element) {
      setTip(null);
      return;
    }
    const rect = element.getBoundingClientRect();
    setTip({ label, top: rect.top + rect.height / 2, left: rect.right + 12 });
  };

  // `/` focuses the menu filter, as the reference's hint advertises. Ignored
  // while the reader is already typing somewhere, or the shortcut would eat
  // the slash out of a URL field.
  useEffect(() => {
    function onKeyDown(event) {
      if (event.key !== '/') return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable) {
        return;
      }
      event.preventDefault();
      searchRef.current?.focus();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return nav;
    return nav
      .map((group) => ({
        ...group,
        // A parent survives when it or any sub-item matches; its sub-items
        // are narrowed to the matches unless the parent itself matched.
        items: group.items
          .map((item) => {
            if (item.label.toLowerCase().includes(term)) return item;
            const children = (item.children ?? []).filter((child) =>
              child.label.toLowerCase().includes(term),
            );
            return children.length ? { ...item, children } : null;
          })
          .filter(Boolean),
      }))
      .filter((group) => group.items.length > 0);
  }, [nav, query]);

  return (
    <>
      <div
        className={cn(
          'flex h-13.5 shrink-0 items-center gap-2 px-3',
          collapsed && 'lg:justify-center lg:px-0',
        )}
      >
        <Link to="/" className="flex min-w-0 items-center gap-2" title={config.appName}>
          {/* The Wonder mark on the brand's night, as the logo ships. */}
          <span className="bg-night text-on-night grid size-7 shrink-0 place-items-center rounded-md">
            <Logo size={16} />
          </span>
          {/* The area under the name rather than beside it: at the compact
              rail's width the two on one line cut the name short. */}
          <span className={cn('min-w-0 leading-tight', full)}>
            <span className="text-ink block truncate text-sm font-semibold">{config.appName}</span>
            {sectionLabel && (
              <span className="text-brand-700 block truncate text-[9px] font-semibold tracking-[0.08em] uppercase">
                {sectionLabel}
              </span>
            )}
          </span>
        </Link>
      </div>

      <div className={cn('px-2.5 pt-2 pb-1', full)}>
        <div className="border-line bg-surface focus-within:border-brand-400 focus-within:ring-brand-100 flex items-center gap-2 rounded-md border px-2 py-1.5 shadow-xs transition focus-within:ring-2">
          <Icon name="search" size={14} className="text-ink-muted shrink-0" />
          <input
            ref={searchRef}
            // Not a form field the browser should fill: without these, a
            // password manager can drop the signed-in email in here.
            type="search"
            name="menu-search"
            autoComplete="off"
            data-1p-ignore
            data-lpignore="true"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search menu"
            aria-label="Search menu"
            className="text-ink placeholder:text-ink-muted/70 min-w-0 flex-1 bg-transparent text-[12px] outline-none [&::-webkit-search-cancel-button]:hidden"
          />
          <kbd className="border-line text-ink-muted/70 hidden rounded border px-1.5 text-[10px] leading-4 sm:block">
            /
          </kbd>
        </div>
      </div>

      <nav
        className="shell-scroll flex-1 overflow-y-auto px-2.5 pb-3"
        aria-label={sectionLabel ?? 'Main'}
        onScroll={() => {
          setTip(null);
          setFlyout(null);
        }}
      >
        {filtered.map((group) => (
          <NavGroup
            key={group.label}
            // By the group's place in the full menu, so a search keeps each
            // group's colour instead of recolouring what survives the filter.
            tone={
              GROUP_TONES[
                Math.max(
                  nav.findIndex((entry) => entry.label === group.label),
                  0,
                ) % GROUP_TONES.length
              ]
            }
            group={group}
            current={current}
            counts={counts}
            forceOpen={query.trim().length > 0}
            collapsed={collapsed}
            onTip={showTip}
            onFlyout={showFlyout}
          />
        ))}
        {filtered.length === 0 && (
          <p className="text-ink-muted px-3 py-6 text-center text-xs">Nothing matches “{query}”.</p>
        )}
      </nav>

      {flyout && (
        <Flyout flyout={flyout} current={current} onEnter={keepFlyout} onLeave={dropFlyout} />
      )}

      {collapsed && tip && (
        <div
          role="tooltip"
          className="bg-night text-on-night pointer-events-none fixed z-[60] hidden -translate-y-1/2 rounded-lg px-2.5 py-1.5 text-xs font-medium whitespace-nowrap shadow-lg lg:block"
          style={{ top: tip.top, left: tip.left }}
        >
          <span
            aria-hidden="true"
            className="bg-night absolute top-1/2 -left-1 size-2 -translate-y-1/2 rotate-45"
          />
          {tip.label}
        </div>
      )}

      <div className="border-line border-t p-2.5">
        <NavLink
          to={profileTo}
          title={collapsed ? (user?.full_name ?? 'Account') : undefined}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-2 rounded-md px-1.5 py-1.5 transition',
              isActive ? 'bg-brand-50' : 'hover:bg-surface-muted',
              collapsed && 'lg:justify-center lg:px-0',
            )
          }
        >
          <span className="bg-brand-600 grid size-7 shrink-0 place-items-center rounded-full text-[10px] font-semibold text-white">
            {initials(user?.full_name)}
          </span>
          <span className={cn('min-w-0 flex-1', full)}>
            <span className="text-ink block truncate text-[12px] font-semibold">
              {user?.full_name ?? 'Account'}
            </span>
            <span className="text-ink-muted block truncate text-[10px]">{roleLabel}</span>
          </span>
          <Icon name="chevron" size={13} className={cn('text-ink-muted shrink-0', full)} />
        </NavLink>
        <button
          type="button"
          onClick={onSignOut}
          title={collapsed ? 'Sign out' : undefined}
          className={cn(
            'text-ink-muted hover:bg-danger-50 hover:text-danger-700 mt-0.5 flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-[12px] transition',
            collapsed && 'lg:justify-center lg:px-0',
          )}
        >
          <Icon name="logout" size={15} className="ml-0.5" />
          <span className={cn(collapsed && 'lg:sr-only')}>Sign out</span>
        </button>
      </div>
    </>
  );
}

// --------------------------------------------------------------------------- //
// Account menu
// --------------------------------------------------------------------------- //

/**
 * The name chip in the top bar, and the menu it opens: who is signed in, the
 * role's own account links (`items`, from the layout) and Sign out.
 *
 * Keyboard: Enter, Space or Arrow Down opens it on the first item; Arrow Up /
 * Down, Home and End move; Escape closes it and returns focus to the chip. An
 * outside click or a navigation closes it too.
 */
function AccountMenu({ user, roleLabel, items, onSignOut }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const location = useLocation();

  const entries = () => [...(rootRef.current?.querySelectorAll('[role="menuitem"]') ?? [])];

  useEffect(() => setOpen(false), [location.pathname, location.hash]);

  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(event) {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  function show(focus = 'first') {
    setOpen(true);
    requestAnimationFrame(() => {
      const list = entries();
      (focus === 'last' ? list[list.length - 1] : list[0])?.focus();
    });
  }

  function close() {
    setOpen(false);
    triggerRef.current?.focus();
  }

  function onKeyDown(event) {
    const list = entries();
    const index = list.indexOf(document.activeElement);
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      close();
    } else if (event.key === 'Tab') {
      setOpen(false);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) show('first');
      else list[(index + 1) % list.length]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) show('last');
      else list[(index - 1 + list.length) % list.length]?.focus();
    } else if (open && event.key === 'Home') {
      event.preventDefault();
      list[0]?.focus();
    } else if (open && event.key === 'End') {
      event.preventDefault();
      list[list.length - 1]?.focus();
    }
  }

  const itemClass =
    'text-ink hover:bg-brand-50 hover:text-brand-700 focus:bg-brand-50 focus:text-brand-700 ' +
    'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] font-medium transition focus:outline-none';

  return (
    <div ref={rootRef} className="relative" onKeyDown={onKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        onClick={() => (open ? setOpen(false) : show())}
        className={cn(
          'hover:bg-surface-muted flex items-center gap-2 rounded-full py-1 pr-2 pl-1 transition',
          open && 'bg-surface-muted',
        )}
      >
        <span className="bg-brand-600 grid size-7 shrink-0 place-items-center rounded-full text-[10px] font-semibold text-white">
          {initials(user?.full_name)}
        </span>
        <span className="hidden text-left leading-tight sm:block">
          <span className="text-ink block text-[12px] font-semibold">
            {user?.full_name?.split(' ')[0] ?? 'Account'}
          </span>
          <span className="text-ink-muted block text-[10px]">{roleLabel}</span>
        </span>
        <Icon
          name="chevron"
          size={13}
          className={cn(
            'text-ink-muted hidden transition-transform sm:block',
            open ? '-rotate-90' : 'rotate-90',
          )}
        />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Account"
          className="border-line bg-surface absolute top-full right-0 z-50 mt-2 w-64 rounded-xl border p-1.5 shadow-lg"
        >
          <div className="border-line mb-1 flex items-center gap-2.5 border-b px-2.5 pt-1.5 pb-3">
            <span className="bg-brand-600 grid size-9 shrink-0 place-items-center rounded-full text-xs font-semibold text-white">
              {initials(user?.full_name)}
            </span>
            <span className="min-w-0">
              <span className="text-ink block truncate text-[13px] font-semibold">
                {user?.full_name ?? 'Account'}
              </span>
              {user?.email && (
                <span className="text-ink-muted block truncate text-[11px]">{user.email}</span>
              )}
              <span className="bg-brand-50 text-brand-700 mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold">
                {roleLabel}
              </span>
            </span>
          </div>

          {items.map((item) => (
            <Link key={item.label} to={item.to} role="menuitem" className={itemClass}>
              <Icon name={item.icon} size={15} className="text-ink-muted" />
              {item.label}
            </Link>
          ))}

          <div className="border-line mt-1 border-t pt-1">
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
              className={cn(
                itemClass,
                'text-danger-700 hover:bg-danger-50 hover:text-danger-700 focus:bg-danger-50 focus:text-danger-700',
              )}
            >
              <Icon name="logout" size={15} />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Shell
// --------------------------------------------------------------------------- //

export function AppShell({
  nav,
  counts,
  sectionLabel,
  roleLabel,
  user,
  profileTo,
  accountMenu,
  onSignOut,
  headerRight,
  banner,
  children,
}) {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useCollapsedRail();

  // A drawer that survived navigation would cover the page it just opened.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  // The account block reaches the profile page, and the nav does not, so
  // without this the breadcrumb there would fall back to the section name and
  // read as if you were on the dashboard.
  const current =
    findCurrent(nav, location.pathname, location.search) ??
    (location.pathname.startsWith(profileTo)
      ? { label: 'My profile' }
      : location.pathname.startsWith('/notifications')
        ? // Shared by every role, so it sits in no role's menu.
          { label: 'Notifications' }
        : undefined);

  return (
    // A column below lg (the sidebar is a fixed drawer there) and a row
    // beside the sidebar from lg up; either way `main` reaches the bottom of
    // the screen, so a page can take the height left with `flex-1`.
    <div className="app-shell bg-surface-muted text-ink flex min-h-screen flex-col lg:flex-row">
      {drawerOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          // White at the top fading to a lavender wash, as in the reference --
          // the white row hover and white icon tiles read against it.
          'border-line to-brand-50 fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r bg-linear-to-b from-white from-30% transition-[transform,width] duration-200 lg:w-50',
          collapsed && 'lg:w-16',
          // z-40 on desktop so the edge toggle can sit over the top bar's edge.
          'lg:sticky lg:top-0 lg:z-40 lg:h-screen lg:translate-x-0',
          drawerOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <button
          type="button"
          onClick={() => setDrawerOpen(false)}
          className="text-ink-muted hover:text-ink absolute top-4 right-3 rounded p-1 lg:hidden"
          aria-label="Close navigation"
        >
          <Icon name="close" size={18} />
        </button>
        {/* The collapse control on the rail itself, where people look for it:
            a round tab on the rail's edge whose arrow points the way it will
            move. The top bar keeps its own toggle too. */}
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-pressed={collapsed}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="bg-surface border-line text-ink-muted hover:border-brand-300 hover:bg-brand-600 hover:text-white absolute top-3.5 -right-3 hidden size-6 place-items-center rounded-full border shadow-sm transition lg:grid"
        >
          <Icon name="chevron" size={12} className={collapsed ? '' : 'rotate-180'} />
        </button>
        <Rail
          nav={nav}
          current={current}
          counts={counts}
          sectionLabel={sectionLabel}
          user={user}
          roleLabel={roleLabel}
          profileTo={profileTo}
          onSignOut={onSignOut}
          collapsed={collapsed}
        />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="border-line bg-surface/85 sticky top-0 z-30 border-b backdrop-blur">
          <div className="mx-auto flex h-13.5 w-full max-w-(--container-console) items-center gap-2.5 px-4 sm:px-5">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="text-ink-muted hover:text-ink -ml-1 rounded-lg p-1.5 lg:hidden"
              aria-label="Open navigation"
            >
              <Icon name="menu" />
            </button>
            <button
              type="button"
              onClick={() => setCollapsed(!collapsed)}
              className="text-ink-muted hover:bg-surface-muted hover:text-ink -ml-1 hidden rounded-lg p-1.5 transition lg:inline-flex"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-pressed={collapsed}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <Icon name={collapsed ? 'panelLeftOpen' : 'panelLeftClose'} size={17} />
            </button>

            <nav aria-label="Breadcrumb" className="min-w-0">
              <ol className="text-ink-muted flex items-center gap-1.5 text-[12px]">
                <li className="hidden sm:block">{sectionLabel}</li>
                <li className="hidden sm:block" aria-hidden="true">
                  <Icon name="chevron" size={11} className="opacity-50" />
                </li>
                <li className="text-ink truncate font-medium">{current?.label ?? sectionLabel}</li>
              </ol>
            </nav>

            <div className="ml-auto flex items-center gap-2">
              {headerRight}
              <AccountMenu
                user={user}
                roleLabel={roleLabel}
                items={accountMenu ?? [{ to: profileTo, label: 'My profile', icon: 'user' }]}
                onSignOut={onSignOut}
              />
            </div>
          </div>
        </header>

        <main className="mx-auto flex w-full max-w-(--container-console) min-w-0 flex-1 flex-col px-4 py-4 sm:px-5 sm:py-5">
          <AnnouncementBanner />
          {banner}
          {children}
        </main>
      </div>
    </div>
  );
}
